import { formatDateTime, formatNumber } from '@dcp/shared/utils/formatters'
import { ConfirmationDialog } from '@/components/admin/ConfirmationDialog'
import { useFileImportTransactionsCount } from '@/hooks/useBanking'
import { useDeleteTransactionImport } from '@/hooks/useTransactionImports'
import type { FileImportRow } from '@/types/banking'

interface DeleteTransactionImportDialogProps {
  fileImport: FileImportRow
  onClose: () => void
}

/**
 * Confirm and delete a completed transaction import with every transaction it
 * inserted. Mount it only while open: the transaction count it quotes is read
 * live on mount (imported transactions may have been deleted one by one on the
 * Manual tab since the import), so Delete stays disabled until that count is in.
 */
export function DeleteTransactionImportDialog({ fileImport, onClose }: DeleteTransactionImportDialogProps) {
  const count = useFileImportTransactionsCount(fileImport.id)
  const deleteImport = useDeleteTransactionImport()
  // Undefined until a fresh count is in; a cached figure is never quoted —
  // a failed re-count keeps the previous `data`, so an error hides it too.
  const liveCount = count.isFetching || count.isError ? undefined : count.data

  const confirm = () => {
    deleteImport.mutate(
      { fileImportId: fileImport.id, fileName: fileImport.file_name },
      { onSuccess: onClose },
    )
  }

  return (
    <ConfirmationDialog
      open
      onOpenChange={(open) => {
        if (!open && !deleteImport.isPending) onClose()
      }}
      title="Delete this import and all its transactions?"
      description={
        <>
          This will <strong className="font-semibold text-foreground">permanently delete</strong> the file{' '}
          <code className="break-all rounded bg-muted px-1 py-0.5 font-mono text-xs text-foreground">
            {fileImport.file_name}
          </code>{' '}
          (imported on {formatDateTime(fileImport.created_at)}) and all{' '}
          {liveCount === undefined ? '…' : formatNumber(liveCount)} transaction{liveCount === 1 ? '' : 's'} imported
          with it. This action cannot be undone.
          {count.isError && (
            <span role="alert" className="mt-2 block text-destructive">
              Couldn&apos;t count the transactions: {count.error.message}
            </span>
          )}
        </>
      }
      confirmText="Delete"
      cancelText="Cancel"
      variant="destructive"
      isLoading={deleteImport.isPending}
      confirmDisabled={liveCount === undefined}
      onConfirm={confirm}
    />
  )
}
